import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import MigrationPanel from "./MigrationPanel";
import SupervisedTestPanel from "./harness/SupervisedTestPanel";


/**
 * Open a harness report in a new tab.  The report endpoint requires a
 * Bearer JWT, and browsers do not attach Authorization headers to
 * `<a href>` navigations.  We fetch the report as a Blob via the
 * authenticated axios client and open it via ``URL.createObjectURL``.
 */
async function openAuthedReport(runId, fmt = "html") {
  try {
    const resp = await api.get(`/harness/runs/${runId}/report?fmt=${fmt}`,
                                        { responseType: "blob" });
    const url = URL.createObjectURL(resp.data);
    const w = window.open(url, "_blank");
    if (!w) toast.error("Popup blocked — allow popups for this site");
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  } catch (e) {
    if (e?.response?.status === 404) toast.error("Report not yet available");
    else toast.error("Failed to load report");
  }
}


const TONE_STYLES = {
  HEALTHY: "pnl-up border-current",
  WARNING: "text-signal-yellow border-current",
  ACTION_REQUIRED: "text-signal-yellow border-current",
  CRITICAL: "pnl-down border-current",
  UNKNOWN: "text-muted-foreground border-border/60",
};

const TONE_DOT = {
  HEALTHY: "🟢",
  WARNING: "🟡",
  ACTION_REQUIRED: "🟠",
  CRITICAL: "🔴",
  UNKNOWN: "⚪",
};

const STATUS_TONE = {
  RECONCILED: "HEALTHY",
  MINOR_DIFFERENCE: "WARNING",
  NEEDS_RECONCILIATION: "ACTION_REQUIRED",
  MANUAL_REVIEW_REQUIRED: "CRITICAL",
};

export default function SystemPage() {
  const [tab, setTab] = useState("health");
  return (
    <div className="p-4 md:p-6 space-y-4" data-testid="system-root">
      <h1 className="text-xl font-medium tracking-tight">System</h1>
      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList className="!rounded-none border-b border-border/60 bg-transparent p-0 h-auto">
          {["health","validation","events","harness","migration"].map((v) => (
            <TabsTrigger key={v} value={v} className="!rounded-none border-b-2 border-transparent data-[state=active]:border-white data-[state=active]:bg-transparent data-[state=active]:text-foreground uppercase tracking-widest text-[10px] mono px-3" data-testid={`system-tab-${v}`}>
              {v}
            </TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="health"><HealthPanel/></TabsContent>
        <TabsContent value="validation"><ValidationPanel/></TabsContent>
        <TabsContent value="events"><EventExplorerPanel/></TabsContent>
        <TabsContent value="harness"><HarnessTabHost/></TabsContent>
        <TabsContent value="migration"><MigrationPanel/></TabsContent>
      </Tabs>
    </div>
  );
}

function SchedulerHeartbeatCard() {
  const [hb, setHb] = useState(null);
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    let stop = false;
    const load = async () => {
      try {
        const { data } = await api.get("/ai-quality/scheduler-heartbeat");
        if (!stop) setHb(data);
      } catch (_e) { /* silent — card just stays in loading state */ }
    };
    load();
    const iv = setInterval(load, 15000);
    const clk = setInterval(() => setNow(Date.now()), 1000);
    return () => { stop = true; clearInterval(iv); clearInterval(clk); };
  }, []);

  if (!hb) return (
    <div className="border border-border/60 p-4" data-testid="scheduler-heartbeat-loading">
      <div className="label-cap">Research Scheduler</div>
      <div className="text-[11px] text-muted-foreground mt-2">Loading…</div>
    </div>
  );
  const tone = TONE_STYLES[hb.status] || TONE_STYLES.UNKNOWN;
  const dot = TONE_DOT[hb.status] || TONE_DOT.UNKNOWN;
  const nextEt = hb.next_slot_et ? new Date(hb.next_slot_et) : null;
  const nextLocal = nextEt ? formatLocalWithEt(nextEt) : "—";
  const nextIn = nextEt ? relDelta(nextEt.getTime() - now) : "—";
  const hbAge = hb.heartbeat_age_seconds != null
    ? `${Math.round(hb.heartbeat_age_seconds)}s ago` : "never";
  const last3 = (hb.last_three_slots || []).slice(0, 3);
  return (
    <div className={`border p-4 ${tone}`} data-testid="scheduler-heartbeat">
      <div className="flex items-start justify-between">
        <div>
          <div className="label-cap">Research Scheduler</div>
          <div className="text-lg font-medium mt-1 mono" data-testid="scheduler-heartbeat-status">
            {dot} {hb.status}
          </div>
        </div>
        <div className="text-right text-[10px] mono text-muted-foreground uppercase tracking-widest">
          <div>last heartbeat: <span className="text-foreground">{hbAge}</span></div>
          <div>worker: <span className="text-foreground">{hb.worker_id || "—"}</span></div>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 mt-3 text-[11px] mono">
        <div>
          <div className="text-muted-foreground uppercase tracking-widest text-[9px]">Next slot</div>
          <div className="text-foreground" data-testid="scheduler-heartbeat-next">
            {hb.next_slot_session || "—"} · {nextLocal}
          </div>
          <div className="text-muted-foreground text-[10px]">in {nextIn}</div>
        </div>
        <div>
          <div className="text-muted-foreground uppercase tracking-widest text-[9px]">
            Env / Flag
          </div>
          <div className="text-foreground">
            scheduler: {hb.scheduler_on ? "ON" : "OFF"} ·
            RESEARCH_SCAN_SCHED_ENABLED={hb.env_flag_value}
          </div>
          <div className="text-muted-foreground text-[10px]">
            trading day: {hb.trading_day_today ? "YES" : "NO"}
          </div>
        </div>
      </div>
      {last3.length > 0 && (
        <div className="mt-3 border-t border-border/40 pt-2">
          <div className="text-muted-foreground uppercase tracking-widest text-[9px] mb-1">
            Last 3 slots · dispatcher / workspace
          </div>
          <div className="grid gap-1.5 text-[11px] mono" data-testid="scheduler-heartbeat-last3">
            {last3.map(c => {
              const dt = c.scheduled_for ? new Date(c.scheduled_for) : null;
              const disp = c.dispatcher_status || c.status || "UNKNOWN";
              const dispFailure = c.dispatcher_failure_reason;
              const dispStale   = c.dispatcher_reconciled_as_stale;
              const wss  = c.workspace_execution_state || c.workspace_status || "UNKNOWN";
              const wsReason = c.workspace_reason;
              const wsDiagnostic = c.workspace_diagnostic;
              const recs = c.workspace_recommendations;
              return (
                <div key={c.execution_key}
                      className="grid grid-cols-[1fr_120px_180px_60px] items-start gap-2 border-b border-border/20 pb-1 last:border-0"
                      data-testid={`slot-row-${c.session}`}>
                  <div className="min-w-0">
                    <div className="text-foreground truncate">{c.session}</div>
                    <div className="text-muted-foreground text-[10px] truncate">
                      {dt ? formatLocalWithEt(dt) : "—"}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-[9px] uppercase tracking-widest text-muted-foreground">Dispatcher</div>
                    <span className={`chip !text-[9px] ${slotStatusTone(disp)}`}
                          data-testid={`slot-dispatcher-${c.session}`}>{disp}</span>
                    <div className="text-[9px] text-muted-foreground mt-0.5 min-h-[12px] truncate"
                          data-testid={`slot-dispatcher-reason-${c.session}`}
                          title={dispFailure || ""}>
                      {dispFailure ? dispatcherFailureLabel(dispFailure) : ""}
                      {dispStale && dispFailure !== "STALE_LEASE_EXPIRED"
                        ? " · reconciled stale"
                        : ""}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-[9px] uppercase tracking-widest text-muted-foreground">Workspace</div>
                    <span className={`chip !text-[9px] ${slotStatusTone(wss)}`}
                          data-testid={`slot-workspace-${c.session}`}>
                      {wss === "NOT_RUN" ? "NOT RUN" : wss}
                    </span>
                    <div className="text-[9px] text-muted-foreground mt-0.5 min-h-[12px] truncate"
                          data-testid={`slot-workspace-reason-${c.session}`}
                          title={wsDiagnostic || wsReason || ""}>
                      {wss === "NOT_RUN"
                        ? "Dispatcher did not complete before lease expiry"
                        : (wsReason ? skipReasonLabel(wsReason) : "")}
                    </div>
                  </div>
                  <div className="text-right text-[10px]">
                    <div className="text-[9px] uppercase tracking-widest text-muted-foreground">Recs</div>
                    <div className="text-foreground">{recs ?? "—"}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}


function slotStatusTone(status) {
  return {
    SUCCESS: "text-signal-green border-signal-green/40",
    RAN: "text-signal-green border-signal-green/40",
    RUNNING: "text-[#0A84FF] border-[#0A84FF]/40",
    CLAIMED: "text-[#0A84FF] border-[#0A84FF]/40",
    PENDING: "text-[#0A84FF] border-[#0A84FF]/40",
    SCHEDULED: "text-muted-foreground border-border/60",
    SKIPPED: "text-signal-yellow border-signal-yellow/40",
    NOT_RUN: "text-muted-foreground border-border/60",
    MISSED: "text-orange-500 border-orange-500/40",
    FAILED: "text-red-500 border-red-500/40",
    DISABLED: "text-muted-foreground border-border/60",
  }[status] || "text-muted-foreground border-border/60";
}


// Humanize a dispatcher-level failure code. Generic: unknown codes
// render as the code itself (Title-Cased) instead of collapsing to
// "Unknown Prerequisite" — dispatcher failure is NEVER a workspace
// prerequisite miss.
function dispatcherFailureLabel(code) {
  if (!code) return "";
  return {
    STALE_LEASE_EXPIRED: "Stale lease expired",
    MISSED_SCHEDULER_UNAVAILABLE: "Scheduler unavailable",
    SCAN_CRASHED: "Dispatcher crashed",
    BUDGET_LIMIT_REACHED: "AI budget limit reached",
  }[code] || code.replace(/_/g, " ").toLowerCase().replace(
    /(^|\s)\S/g, s => s.toUpperCase());
}


function skipReasonLabel(reason) {
  if (!reason) return "";
  return {
    NO_ACTIVE_BROKER: "No Active Broker",
    NO_ANALYST_CONFIG: "Analyst Config Missing",
    NO_ACTIVE_SCANNER_POLICY: "No Active Scanner Policy",
    AI_BUDGET_LIMIT: "AI Budget Limit Reached",
    WORKSPACE_DISABLED: "Workspace Disabled",
    MARKET_DATA_UNAVAILABLE: "Market Data Unavailable",
    BROKER_STATE_INCONSISTENT: "Broker State Inconsistent",
    CONFIG_INVALID: "Configuration Invalid",
    UNKNOWN_PREREQUISITE: "Unknown Prerequisite",
    MISSED_SCHEDULER_UNAVAILABLE: "Scheduler Unavailable",
    SCAN_CRASHED: "Analyst Provider Error",
    BUDGET_LIMIT_REACHED: "AI Budget Limit Reached",
  }[reason] || reason.replace(/_/g, " ");
}


/** Format `Date` as "MMM D, YYYY · H:MM AM/PM ZZZ (H:MM ET)". Both
 * local + ET labels derive from the SAME canonical instant so
 * midnight and DST boundaries never emit mismatched pairs. */
function formatLocalWithEt(dt) {
  const dateStr = dt.toLocaleDateString("en-US",
    { month: "short", day: "numeric", year: "numeric" });
  const local = dt.toLocaleTimeString("en-US",
    { hour: "numeric", minute: "2-digit", hour12: true,
      timeZoneName: "short" });
  const et = dt.toLocaleTimeString("en-US",
    { hour: "numeric", minute: "2-digit", hour12: true,
      timeZone: "America/New_York" });
  const timeStr = local.includes("EDT") || local.includes("EST")
    ? local
    : `${local} (${et} ET)`;
  return `${dateStr} · ${timeStr}`;
}


/** "in 2h 31m" / "in 47s" / "45s ago". */
function relDelta(ms) {
  const abs = Math.abs(ms);
  const sec = Math.floor(abs / 1000);
  const min = Math.floor(sec / 60);
  const hr  = Math.floor(min / 60);
  let out;
  if (hr > 0)      out = `${hr}h ${min % 60}m`;
  else if (min > 0) out = `${min}m ${sec % 60}s`;
  else              out = `${sec}s`;
  return ms < 0 ? `${out} ago` : out;
}


function HealthPanel() {
  const [health, setHealth] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/broker/ledger/system/health");
      setHealth(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Health load failed"); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, []);

  if (!health) return <div className="mt-6 text-sm text-muted-foreground" data-testid="health-empty">Loading system health…</div>;
  const overall = health.overall || "UNKNOWN";
  return (
    <div className="mt-6 space-y-4" data-testid="health-panel">
      <div className={`border p-4 ${TONE_STYLES[overall]}`} data-testid="health-overall">
        <div className="label-cap">System Overall</div>
        <div className="text-2xl font-medium mt-1 mono">{TONE_DOT[overall]} {overall.replace("_", " ")}</div>
        <div className="text-[11px] text-muted-foreground mt-1">as of {new Date(health.computed_at).toLocaleString()}</div>
        {(health.environment || health.database_name) && (
          <div className="text-[10px] mono text-muted-foreground mt-1 uppercase tracking-widest"
               data-testid="health-env-db">
            env: <span className={health.environment === "production" ? "text-red-500" : "text-signal-blue"}>{health.environment || "—"}</span>
            {" · "}
            db: <span className="text-foreground">{health.database_name || "—"}</span>
          </div>
        )}
      </div>
      <SchedulerHeartbeatCard />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-border/60">
        {(health.checks || []).map(c => {
          const t = c.key === "last_sync" ? relTime(c.state) : null;
          return (
          <div key={c.key} className={`grid-cell ${TONE_STYLES[c.tone]}`} data-testid={`health-check-${c.key}`}>
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="label-cap">{c.label}</div>
                <div className="text-sm mt-1 mono">
                  {c.key === "last_sync" ? (t?.rel || "—") : (c.state ?? "—")}
                </div>
                {c.key === "last_sync" && t?.abs && (
                  <div className="text-[10px] text-muted-foreground">{t.abs}</div>
                )}
                {c.meta?.differences !== undefined && (
                  <div className="text-[11px] text-muted-foreground mt-1">{c.meta.differences} differences · run {c.meta.id?.slice(-8)}</div>
                )}
                {c.key === "broker_connection" && c.meta && (
                  <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-0.5 text-[10px] mono text-muted-foreground">
                    {c.meta.mode && <div>Broker: <span className="text-foreground uppercase">{c.meta.mode.replace("_"," ")}</span></div>}
                    {c.meta.account && <div>Account: <span className="text-foreground">{c.meta.account}</span></div>}
                    {c.meta.margin_state && <div>Margin: <span className="text-signal-blue">{c.meta.margin_state}</span></div>}
                    {c.meta.buying_power != null && <div>BP: <span className="text-foreground">${Number(c.meta.buying_power).toLocaleString(undefined,{minimumFractionDigits:2})}</span></div>}
                    {c.meta.cash != null && <div>Cash: <span className="text-foreground">${Number(c.meta.cash).toLocaleString(undefined,{minimumFractionDigits:2})}</span></div>}
                    {c.meta.equity != null && <div>Equity: <span className="text-foreground">${Number(c.meta.equity).toLocaleString(undefined,{minimumFractionDigits:2})}</span></div>}
                  </div>
                )}
                {c.key === "protection" && c.meta && (
                  <div className="mt-1 text-[10px] mono text-muted-foreground">
                    Protected Positions: <span className="text-foreground">{c.meta.protected_positions ?? 0} / {c.meta.open_positions ?? 0}</span>
                  </div>
                )}
              </div>
              <div className="text-lg">{TONE_DOT[c.tone]}</div>
            </div>
          </div>
        );})}
      </div>
      <button className="btn-outline" onClick={load} disabled={busy} data-testid="health-refresh">{busy ? "Refreshing…" : "Refresh"}</button>
    </div>
  );
}

function ValidationPanel() {
  const [runs, setRuns] = useState([]);
  const [running, setRunning] = useState(false);
  const [selected, setSelected] = useState(null);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    const { data } = await api.get("/broker/ledger/validation");
    setRuns(data.runs || []);
  };
  useEffect(() => { load(); }, []);

  const runNow = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/broker/ledger/validation/run");
      const failing = (data.differences || []).filter(d => d.severity !== "INFORMATIONAL").length;
      const info = (data.differences || []).filter(d => d.severity === "INFORMATIONAL").length;
      const suffix = info > 0
        ? `${failing} diff${failing === 1 ? "" : "s"} · ${info} informational`
        : `${failing} diff${failing === 1 ? "" : "s"}`;
      toast.success(`Validation ${data.status.replace("_"," ")} — ${suffix}`);
      await load();
      setSelected(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Validation run failed"); }
    finally { setRunning(false); }
  };

  const now = Date.now();
  const filtered = runs.filter(r => {
    const t = r.started_at ? new Date(r.started_at).getTime() : 0;
    const age = now - t;
    if (filter === "today")      return age < 24 * 3600e3;
    if (filter === "yesterday")  return age >= 24 * 3600e3 && age < 48 * 3600e3;
    if (filter === "7d")         return age < 7 * 24 * 3600e3;
    if (filter === "failed")     return r.status && r.status !== "RECONCILED";
    if (filter === "info")       return (r.informational_warnings || []).length > 0;
    return true;
  });

  const FILTERS = [
    ["all",       "All"],
    ["today",     "Today"],
    ["yesterday", "Yesterday"],
    ["7d",        "Last 7d"],
    ["failed",    "Failed only"],
    ["info",      "Informational only"],
  ];

  return (
    <div className="mt-6 space-y-4" data-testid="validation-panel">
      <div className="flex items-center gap-2 flex-wrap">
        <button className="btn-danger" onClick={runNow} disabled={running} data-testid="validation-run-btn">
          {running ? "Running…" : "Run Validation Now"}
        </button>
        <div className="text-[11px] text-muted-foreground flex-1">Immutable historical log — persist to answer &ldquo;when did the ledger first go out of balance?&rdquo;</div>
        <div className="flex gap-1" data-testid="validation-history-filters">
          {FILTERS.map(([k, label]) => (
            <button key={k}
                onClick={() => setFilter(k)}
                className={`px-2 py-0.5 text-[10px] mono uppercase tracking-widest border ${
                  filter === k ? "border-current text-signal-blue" : "border-border/60 text-muted-foreground hover:text-foreground"}`}
                data-testid={`validation-filter-${k}`}>
              {label}
            </button>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-12 gap-4">
        <div className="col-span-5 border border-border/60" data-testid="validation-runs-list">
          <div className="p-2 label-cap border-b border-border/60">History ({filtered.length} of {runs.length})</div>
          {filtered.length === 0 && (
            <div className="p-3 text-sm text-muted-foreground italic">No matching runs for this filter.</div>
          )}
          {filtered.map(r => {
            const tone = STATUS_TONE[r.status] || "UNKNOWN";
            return (
              <div key={r.id} className={`p-2 text-xs border-b border-border/40 cursor-pointer ${selected?.id === r.id ? "bg-border/30" : ""}`} onClick={() => setSelected(r)} data-testid={`validation-run-${r.id.slice(-8)}`}>
                <div className="flex justify-between items-center">
                  <span className="mono">{new Date(r.started_at).toLocaleString()}</span>
                  <span className={`chip !text-[9px] ${TONE_STYLES[tone]}`}>{TONE_DOT[tone]} {r.status.replace("_"," ")}</span>
                </div>
                <div className="text-[10px] text-muted-foreground mt-1 flex items-center gap-2">
                  {(() => {
                    const diffs = r.differences || [];
                    const failing = diffs.filter(d => d.severity !== "INFORMATIONAL").length;
                    const info = diffs.filter(d => d.severity === "INFORMATIONAL").length;
                    return (
                      <span>
                        {failing} diff{failing === 1 ? "" : "s"}
                        {info > 0 && <span className="text-muted-foreground/70"> · {info} info</span>}
                        {" · "}{r.manual_review_items || 0} manual · {r.duration_secs}s
                      </span>
                    );
                  })()}
                  {r.trigger && <span className="ml-auto uppercase tracking-widest">{r.trigger}</span>}
                </div>
              </div>
            );
          })}
        </div>
        <div className="col-span-7 border border-border/60">
          {!selected && <div className="p-3 text-sm text-muted-foreground italic">Select a run.</div>}
          {selected && <ValidationRunDetail run={selected}/>}
        </div>
      </div>
    </div>
  );
}

function ValidationRunDetail({ run }) {
  const tone = STATUS_TONE[run.status] || "UNKNOWN";
  const bpInfo = (run.informational_warnings || []).find(d => d.field === "buying_power");
  const marginState = run.broker_snapshot?.margin_state || run.broker_snapshot?.margin_profile?.state_label;
  return (
    <div className="p-3 space-y-3 text-sm" data-testid="validation-detail">
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="label-cap">Run</div>
          <div className="mono text-xs">{run.id}</div>
          <div className={`inline-block chip mt-1 ${TONE_STYLES[tone]}`}>{TONE_DOT[tone]} {run.status.replace("_"," ")}</div>
        </div>
        <div className="text-right text-[10px] mono text-muted-foreground">
          <div>Triggered by <span className="text-foreground">{run.trigger || "unknown"}</span></div>
          <div>{new Date(run.started_at).toLocaleString()}</div>
        </div>
      </div>

      {/* Hard-reconciliation grid: cash / equity / positions.
          Informational differences (cash/equity within ±$0.05) are
          rendered in yellow "within tolerance" style rather than red,
          so the user can see the drift without treating it as a
          reconciliation failure. */}
      <div className="grid grid-cols-3 gap-2 text-xs">
        {["cash","equity","positions_count"].map(k => {
          const br = run.broker_snapshot?.[k];
          const tm = run.trademind_derived?.[k];
          const diff = (run.differences || []).find(d => d.field === k);
          const isInfo = diff?.severity === "INFORMATIONAL";
          return (
            <div key={k} className="border border-border/60 p-2">
              <div className="label-cap">{k.replace("_"," ")}</div>
              <div className="mono">Broker: {typeof br === "number" ? br.toLocaleString(undefined,{minimumFractionDigits:2}) : br}</div>
              <div className="mono">WingWise: {typeof tm === "number" ? tm.toLocaleString(undefined,{minimumFractionDigits:2}) : tm}</div>
              {diff && (
                <div className={`mono text-[10px] ${isInfo ? "text-signal-yellow" : "pnl-down"}`}>
                  Δ {diff.delta.toLocaleString(undefined,{minimumFractionDigits:2})}
                  {isInfo && <span className="ml-1 text-[9px] uppercase tracking-widest">within tolerance</span>}
                </div>
              )}
              {!diff && <div className="text-[9px] mono text-muted-foreground">MATCHED</div>}
            </div>
          );
        })}
      </div>

      {/* Buying Power — narrative-style informational card */}
      {run.broker_snapshot?.buying_power != null && (
        <div className="border border-signal-yellow/40 bg-signal-yellow/5 p-3" data-testid="validation-buying-power">
          <div className="flex items-center justify-between">
            <div className="label-cap text-signal-yellow">Broker Buying Power · Informational Only</div>
            {marginState && (
              <span className="chip !text-[9px] text-signal-blue border-current">{marginState}</span>
            )}
          </div>
          <div className="mono text-lg mt-1">${Number(run.broker_snapshot.buying_power).toLocaleString(undefined,{minimumFractionDigits:2})}</div>
          <div className="text-[11px] text-muted-foreground mt-1">
            {bpInfo?.note || "Broker buying power is broker margin capacity. Ledger accounting uses actual cash."}
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 gap-2 text-[11px]">
        <div>Outstanding Orders: <span className="mono">{run.outstanding_orders}</span></div>
        <div>Open Mismatches: <span className="mono">{run.open_mismatches}</span></div>
        <div>Critical Mismatches: <span className="mono pnl-down">{run.critical_mismatches}</span></div>
        <div>Manual Review: <span className="mono">{run.manual_review_items}</span></div>
        <div>Auto Repairs Total: <span className="mono">{run.auto_repairs_total}</span></div>
        <div>Missing Fills: <span className="mono">{run.missing_fills}</span></div>
        <div>Duplicate Events: <span className="mono">{run.duplicate_events}</span></div>
        <div>Reservations Open: <span className="mono">${(run.trademind_derived?.reservations_open_cash ?? 0).toLocaleString(undefined,{minimumFractionDigits:2})}</span></div>
      </div>
    </div>
  );
}

function EventExplorerPanel() {
  const [filters, setFilters] = useState({ symbol: "", event_type: "", broker_order_id: "", errors_only: false });
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [trace, setTrace] = useState(null);

  const search = async () => {
    setBusy(true);
    try {
      const p = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) p.set(k, v); });
      const { data } = await api.get(`/broker/ledger/explorer/events?${p.toString()}`);
      setRows(data.entries || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Search failed"); }
    finally { setBusy(false); }
  };
  useEffect(() => { search(); }, []);

  const openTrace = async (row) => {
    if (row.kind !== "ledger" || !row.id) { toast.error("Trace only available for ledger events"); return; }
    try {
      const { data } = await api.get(`/broker/ledger/event/${row.id}/trace`);
      setTrace(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Trace failed"); }
  };

  return (
    <div className="mt-6 space-y-3" data-testid="explorer-panel">
      <div className="grid grid-cols-5 gap-2">
        <input className="input text-xs" placeholder="symbol" value={filters.symbol} onChange={(e)=>setFilters({...filters,symbol:e.target.value.toUpperCase()})} data-testid="explorer-symbol"/>
        <input className="input text-xs" placeholder="event_type" value={filters.event_type} onChange={(e)=>setFilters({...filters,event_type:e.target.value})} data-testid="explorer-event-type"/>
        <input className="input text-xs" placeholder="broker_order_id" value={filters.broker_order_id} onChange={(e)=>setFilters({...filters,broker_order_id:e.target.value})} data-testid="explorer-broker-order"/>
        <label className="flex items-center gap-2 text-xs">
          <input type="checkbox" checked={filters.errors_only} onChange={(e)=>setFilters({...filters,errors_only:e.target.checked})} data-testid="explorer-errors-only"/>
          Errors only
        </label>
        <button className="btn-outline text-xs" onClick={search} disabled={busy} data-testid="explorer-search-btn">{busy ? "…" : "Search"}</button>
      </div>
      <div className="border border-border/60">
        <div className="grid grid-cols-6 gap-2 p-2 label-cap border-b border-border/60 text-[10px]">
          <div>Time</div>
          <div>Kind</div>
          <div>Type / Event</div>
          <div>Symbol</div>
          <div className="text-right">Amount</div>
          <div className="text-right">Actions</div>
        </div>
        {rows.length === 0 && <div className="p-3 text-sm text-muted-foreground italic">No entries.</div>}
        {rows.map((r,i) => (
          <div key={i} className="grid grid-cols-6 gap-2 p-2 text-xs border-b border-border/40 last:border-b-0" data-testid={`explorer-row-${i}`}>
            <div className="mono text-[10px]">{new Date(r.at).toLocaleString()}</div>
            <div><span className={`chip !text-[9px] ${r.kind === "ledger" ? "pnl-up border-current" : r.kind === "mismatch" ? "text-signal-yellow border-current" : "text-muted-foreground border-current"}`}>{r.kind}</span></div>
            <div className="mono truncate">{r.event_type || r.event || r.category}</div>
            <div className="mono">{r.symbol || "—"}</div>
            <div className="text-right mono">{r.amount_cash !== undefined ? `$${Number(r.amount_cash).toLocaleString(undefined,{minimumFractionDigits:2})}` : "—"}</div>
            <div className="text-right">
              {r.kind === "ledger" && <button className="btn-outline !text-[10px] !px-2 !py-0.5" onClick={()=>openTrace(r)} data-testid={`explorer-trace-${i}`}>View Timeline</button>}
            </div>
          </div>
        ))}
      </div>
      {trace && <TraceTimeline trace={trace} onClose={()=>setTrace(null)}/>}
    </div>
  );
}

function TraceTimeline({ trace, onClose }) {
  const steps = [
    trace.proposal && { at: trace.proposal.created_at, label: "Proposal Created", data: trace.proposal, kind: "proposal" },
    trace.order_intent && { at: trace.order_intent.created_at, label: "Order Intent", data: trace.order_intent, kind: "intent" },
    ...(trace.broker_events || []).map(be => ({ at: be.received_at, label: `Stream: ${be.event}`, data: be, kind: "broker_event" })),
    trace.fill_record && { at: trace.fill_record.fill_timestamp, label: "Fill Record", data: trace.fill_record, kind: "fill" },
    trace.broker_position && { at: trace.broker_position.opened_at, label: "Position", data: trace.broker_position, kind: "position" },
    ...(trace.ledger_events || []).map(le => ({ at: le.processing_timestamp, label: `Ledger: ${le.event_type}`, data: le, kind: "ledger" })),
    ...(trace.reconciliation || []).map(rr => ({ at: rr.last_detected_at, label: `Reconciliation: ${rr.category}:${rr.field}`, data: rr, kind: "reconciliation" })),
  ].filter(Boolean).sort((a,b) => (a.at || "").localeCompare(b.at || ""));

  return (
    <div className="fixed inset-0 z-[85] flex items-center justify-center bg-black/70 backdrop-blur-sm p-6" data-testid="trace-modal" onClick={(e)=>{ if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-background border border-border/80 max-w-4xl w-full max-h-[85vh] overflow-y-auto">
        <div className="p-4 border-b border-border/60 flex justify-between items-center">
          <div>
            <div className="label-cap">Trace</div>
            <div className="text-sm mono">{trace.event?.id} · {trace.event?.event_type}</div>
          </div>
          <button className="btn-outline" onClick={onClose} data-testid="trace-close">Close</button>
        </div>
        <div className="p-4 space-y-2">
          {steps.map((s,i) => (
            <details key={i} className="border border-border/60 p-2" data-testid={`trace-step-${i}`}>
              <summary className="cursor-pointer flex justify-between items-center text-sm">
                <span>{i+1}. {s.label}</span>
                <span className="text-[10px] text-muted-foreground mono">{s.at ? new Date(s.at).toLocaleString() : "—"}</span>
              </summary>
              <pre className="mt-2 text-[10px] mono bg-border/20 p-2 overflow-x-auto">{JSON.stringify(s.data, null, 2)}</pre>
            </details>
          ))}
          {steps.length === 0 && <div className="text-sm text-muted-foreground italic">No lineage steps recorded yet.</div>}
        </div>
      </div>
    </div>
  );
}

// --------------------------------------------------------------------- //
// Harness Panel — CI + Scheduled Harness Automation status
// --------------------------------------------------------------------- //

const GATE_TONE = { PASS: "HEALTHY", WARNING: "WARNING", BLOCK: "CRITICAL" };
const OVERALL_TONE = {
  PASS: "HEALTHY", WARNING: "WARNING", FAIL: "CRITICAL",
  SKIPPED: "UNKNOWN", PENDING_IMPLEMENTATION: "UNKNOWN",
  MISSING: "CRITICAL", NOT_CONFIGURED: "UNKNOWN",
};

const HARNESS_LABELS = {
  paper_trading_mock:  "Paper Trading Mock",
  failure_injection:   "Failure Injection Suite",
  soak_short:          "Short Soak",
  soak_long:           "Weekly Soak",
  paper_trading_live:  "Canonical Live Paper",
};

function humanReadyRow(code) {
  // Translate internal codes → operator-friendly title + hint.
  if (!code) return { title: code, hint: "" };
  if (code.startsWith("missing_run:")) {
    const sid = code.split(":")[1];
    return {
      title: `${HARNESS_LABELS[sid] || sid} has not been executed.`,
      hint: "Run it once to unblock deployment readiness.",
      run_sid: sid,
    };
  }
  if (code.startsWith("stale:")) {
    const [, sid, secs] = code.split(":");
    return {
      title: `${HARNESS_LABELS[sid] || sid} last run is stale.`,
      hint: `Result is ${Math.round(parseInt(secs) / 3600)}h old — re-run to refresh.`,
      run_sid: sid,
    };
  }
  if (code.startsWith("cleanup_failed:")) {
    const sid = code.split(":")[1];
    return {
      title: `${HARNESS_LABELS[sid] || sid} left residue behind.`,
      hint: "Open Positions or Order-Intents to clear harness residue.",
    };
  }
  if (code.startsWith("ledger_")) {
    return {
      title: "Ledger validation needs a fresh run.",
      hint: "Open the Validation tab and click Run Validation.",
    };
  }
  if (code.startsWith("critical_mismatches:")) {
    return {
      title: `${code.split(":")[1]} critical broker mismatch(es) are open.`,
      hint: "Open the Broker tab to review and resolve.",
    };
  }
  if (code.startsWith("open_harness_position:")) {
    return { title: "Prior harness left an open test position.",
                hint: "Close it manually before re-running." };
  }
  if (code.startsWith("operator_blocked:")) {
    return { title: "Operator blocked deployment.",
                hint: code.split(":").slice(1).join(":") };
  }
  return { title: code, hint: "" };
}

function humanReadyDetail(detail) {
  // Rich renderer for a block_details object (from evaluate_deploy_gate).
  // Falls back to the string-code path when no detail is provided.
  if (!detail) return null;
  if (detail.kind === "harness_failure" && detail.diagnostic) {
    const d = detail.diagnostic;
    const sid = detail.schedule_id;
    const label = HARNESS_LABELS[sid] || sid;
    const scenario = d.scenario_index && d.scenario_total
      ? `Scenario ${d.scenario_index} of ${d.scenario_total}` : null;
    return {
      title: label,
      scenario,
      subtitle: d.scenario_title || d.step,
      reason: d.root_cause || d.actual || d.detail,
      run_id: detail.run_id,
      run_sid: sid,
      injected_fault: d.injected_fault,
      expected: d.expected,
      actual: d.actual,
      recovery_attempted: d.recovery_attempted,
      recovery_successful: d.recovery_successful,
      recommended_action: d.recommended_action,
    };
  }
  return null;
}

function relTime(iso) {
  if (!iso) return { rel: "—", abs: "" };
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return { rel: iso, abs: "" };
  const diff = Math.max(0, Date.now() - t);
  const s = Math.round(diff / 1000);
  let rel;
  if (s < 60) rel = `${s}s ago`;
  else if (s < 3600) rel = `${Math.round(s / 60)}m ago`;
  else if (s < 86400) rel = `${Math.round(s / 3600)}h ago`;
  else rel = `${Math.round(s / 86400)}d ago`;
  const abs = new Date(iso).toLocaleString();
  return { rel, abs };
}

function DeploymentGateDiagnostics({ gate, liveRow, onUnblock }) {
  const [open, setOpen] = useState(false);
  const [unblocking, setUnblocking] = useState(false);
  if (!gate) return null;

  const gateStatus = gate.status || "UNKNOWN";
  const blocks = gate.blocks || [];
  const warnings = gate.warnings || [];
  const harnesses = gate.harnesses || [];
  const operatorBlock = (blocks.find(b => b.startsWith("operator_blocked:")) || "")
    .replace(/^operator_blocked:/, "");

  // Every gate rule, expressed as a row. `status` ∈ PASS / WARN / FAIL.
  const rows = [];
  (liveRow.requirements || []).forEach(r => {
    rows.push({
      code: r.key,
      label: r.label,
      status: r.met ? "PASS" : "FAIL",
      detail: r.gate_blocks && r.gate_blocks.length
        ? `Blocks: ${r.gate_blocks.join(", ")}`
        : r.gate_warnings && r.gate_warnings.length
          ? `Advisory warnings: ${r.gate_warnings.join(", ")}`
          : null,
    });
  });
  harnesses.forEach(h => {
    // Skip harnesses that don't gate the deploy (e.g. the live paper
    // harness itself — its state is already shown by the badge above,
    // and it will always read NOT_RUN before the first supervised run).
    if (!h.required_for_deploy) return;
    const overall = (h.row && h.row.overall) || h.result || "MISSING";
    const ageH = h.age_secs != null ? (h.age_secs / 3600).toFixed(1) : null;
    const status = overall === "PASS" ? "PASS"
                       : overall === "FAIL" ? "FAIL"
                       : overall === "WARNING" ? "WARN"
                       : overall === "MISSING" ? "FAIL"
                       : "WARN";
    rows.push({
      code: `harness:${h.schedule_id}`,
      label: `Harness · ${HARNESS_LABELS[h.schedule_id] || h.schedule_id}`,
      status,
      detail: ageH != null ? `Last run ${ageH}h ago (${overall})` : `Result ${overall}`,
    });
  });
  blocks.forEach(code => {
    // Only show blocks that aren't already reflected in a harness row.
    if (code.startsWith("stale:") || code.includes(":stale") || code === "missing_run") return;
    rows.push({
      code,
      label: `Block · ${code.split(":")[0].replace(/_/g," ")}`,
      status: "FAIL",
      detail: code,
    });
  });
  warnings.forEach(code => {
    rows.push({
      code: `warn:${code}`,
      label: `Advisory · ${code.split(":")[0].replace(/_/g," ")}`,
      status: "WARN",
      detail: code + " · informational, does not lock",
    });
  });

  const failing = rows.filter(r => r.status === "FAIL");
  const advisory = rows.filter(r => r.status === "WARN");

  return (
    <div className="mt-2 pt-2 border-t border-border/40 space-y-1"
         data-testid="gate-diagnostics">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center justify-between w-full text-[10px] uppercase tracking-widest text-muted-foreground hover:text-foreground"
        data-testid="gate-diagnostics-toggle">
        <span>Deployment Gate Diagnostics</span>
        <span className="mono">
          {gateStatus} · {failing.length} failing · {advisory.length} advisory
          <span className="ml-2">{open ? "▲" : "▼"}</span>
        </span>
      </button>
      {open && (
        <div className="pt-1 space-y-1">
          {rows.map((r, i) => (
            <div key={i} className="grid grid-cols-[16px_1fr_auto] items-start gap-2 text-[10px]"
                 data-testid={`gate-rule-${r.code}`}>
              <span className={r.status === "PASS" ? "text-emerald-500"
                                     : r.status === "WARN" ? "text-signal-yellow"
                                     : "pnl-down"}>
                {r.status === "PASS" ? "✓" : r.status === "WARN" ? "△" : "✗"}
              </span>
              <div>
                <div>{r.label}</div>
                {r.detail && (
                  <div className="text-muted-foreground text-[9px] mt-0.5 mono">{r.detail}</div>
                )}
              </div>
              <span className={`mono uppercase tracking-widest ${
                r.status === "PASS" ? "text-emerald-500"
                : r.status === "WARN" ? "text-signal-yellow"
                : "pnl-down"}`}>{r.status}</span>
            </div>
          ))}
          <div className="pt-1 mt-1 border-t border-border/30 text-[9px] text-muted-foreground">
            {gateStatus === "BLOCK"
              ? "Canonical Live is LOCKED — resolve every FAIL above."
              : gateStatus === "WARNING"
                ? "Canonical Live may be enabled — the advisory warnings are informational and do not lock the harness."
                : "All gate rules PASS."}
          </div>
          {operatorBlock && (
            <div className="mt-2 pt-2 border-t border-border/30 space-y-1.5"
                 data-testid="operator-block-panel">
              <div className="text-[10px] pnl-down font-medium">
                ⚠ Operator-block flag is set.
              </div>
              <div className="text-[9px] mono text-muted-foreground">
                {operatorBlock}
              </div>
              <div className="text-[9px] text-muted-foreground">
                A prior canonical_live run auto-set this flag after a
                CRITICAL failure. After reviewing the failed run, click
                Unblock to allow supervised runs to resume.
              </div>
              <button
                onClick={async () => {
                  setUnblocking(true);
                  try {
                    await api.post("/harness/deploy-unblock");
                    toast.success("Deployment unblocked");
                    if (onUnblock) await onUnblock();
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || "Unblock failed");
                  } finally { setUnblocking(false); }
                }}
                disabled={unblocking}
                className="border border-emerald-500 px-2 py-0.5 text-[10px] uppercase tracking-widest text-emerald-500 hover:bg-emerald-500/10 disabled:opacity-40"
                data-testid="operator-unblock-btn">
                {unblocking ? "Unblocking…" : "Unblock Deployment"}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


function HarnessTabHost() {
  const [view, setView] = useState("supervised");
  return (
    <div className="space-y-3">
      <div className="flex gap-1 text-[10px] mono uppercase tracking-widest">
        <button
          onClick={() => setView("supervised")}
          className={`px-3 py-1 border ${view === "supervised" ? "border-current" : "border-border/60 text-muted-foreground"}`}
          data-testid="harness-tab-supervised">
          Supervised Test
        </button>
        <button
          onClick={() => setView("advanced")}
          className={`px-3 py-1 border ${view === "advanced" ? "border-current" : "border-border/60 text-muted-foreground"}`}
          data-testid="harness-tab-advanced">
          Advanced (developer)
        </button>
      </div>
      {view === "supervised" ? <SupervisedTestPanel/> : <HarnessPanel/>}
    </div>
  );
}


function HarnessPanel() {
  const [schedules, setSchedules] = useState([]);
  const [gate, setGate] = useState(null);
  const [sHealth, setSHealth] = useState(null);
  const [runs, setRuns] = useState([]);
  const [running, setRunning] = useState(null);
  const [expandedRun, setExpandedRun] = useState(null);
  const [canonicalLive, setCanonicalLive] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null); // 'enable' | 'disable' | null

  const load = async () => {
    try {
      const [s, g, h, r, cl] = await Promise.all([
        api.get("/harness/schedules"),
        api.get("/harness/deploy-gate"),
        api.get("/harness/scheduler-health"),
        api.get("/harness/runs?limit=20"),
        api.get("/harness/canonical-live/status"),
      ]);
      setSchedules(s.data.schedules || []);
      setGate(g.data); setSHealth(h.data); setRuns(r.data.runs || []);
      setCanonicalLive(cl.data);
    } catch (e) { toast.error("Failed to load harness status"); }
  };
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, []);

  const flipCanonicalLive = async (enable) => {
    setConfirmAction(null);
    try {
      const r = await api.post(`/harness/canonical-live/${enable ? "enable" : "disable"}`);
      if (r.data.ok) {
        toast.success(enable ? "Canonical Live enabled" : "Canonical Live disabled");
        await load();
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Toggle failed";
      toast.error(msg);
    }
  };

  const runNow = async (sid) => {
    setRunning(sid);
    try {
      const r = await api.post(`/harness/run/${sid}`);
      if (r.data.skipped) toast.warning(r.data.note || `Skipped: ${r.data.reason}`);
      else if (r.data.scheduled) toast.success(r.data.note || `${sid} started in background`);
      else if (r.data.ok) toast.success(`${sid} → ${r.data.overall}`);
      else toast.error(`Failed: ${r.data.error || "unknown"}`);
      await load();
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Trigger failed";
      toast.error(msg);
    }
    setRunning(null);
  };

  const runPRGate = async () => {
    setRunning("pr_gate");
    try {
      const r = await api.post("/harness/pr-gate");
      const overall = r.data.overall;
      if (overall === "PASS") toast.success("PR Gate → PASS");
      else if (overall === "WARNING") toast.warning("PR Gate → WARNING");
      else toast.error(`PR Gate → ${overall}`);
    } catch (e) { toast.error("PR gate failed"); }
    setRunning(null);
  };

  // Readiness derived from Deployment Gate.  Required + optional split.
  const requiredSchedules = ["paper_trading_mock", "failure_injection", "soak_short"];
  const readinessRows = requiredSchedules.map(sid => {
    const s = schedules.find(x => x.schedule_id === sid) || {};
    const last = s.last_run || {};
    return {
      sid, label: HARNESS_LABELS[sid] || sid,
      status: last.overall || (last.status || "MISSING"),
      last_run_at: last.started_at,
      run_id: last.id,
    };
  });
  const liveRow = (() => {
    const s = schedules.find(x => x.schedule_id === "paper_trading_live") || {};
    const last = s.last_run || {};
    const cl = canonicalLive || {};
    const requirements = cl.requirements || [];
    const allMet = requirements.length > 0 && requirements.every(r => r.met);
    let state = "LOCKED";       // default when reqs not met
    if (cl.running_run_id) state = "RUNNING";
    else if (cl.enabled) state = allMet ? "ENABLED" : "ENABLED_BLOCKED";
    else if (allMet) state = "DISABLED";
    return {
      sid: "paper_trading_live",
      label: HARNESS_LABELS.paper_trading_live,
      pending: s.pending_implementation,
      status: last.overall || (s.pending_implementation ? "DISABLED" : "—"),
      last_run_at: last.started_at,
      last_run_id: last.id,
      state,
      enabled: !!cl.enabled,
      updated_at: cl.updated_at,
      updated_by: cl.updated_by,
      running_run_id: cl.running_run_id,
      requirements,
      all_requirements_met: allMet,
    };
  })();

  const readinessTone = gate?.status === "PASS" ? "HEALTHY"
                                    : gate?.status === "WARNING" ? "HEALTHY"
                                    : "CRITICAL";
  const readinessLabel = gate?.status === "PASS"
      ? "🟢 Ready for supervised paper testing"
      : gate?.status === "WARNING"
        ? "🟢 Ready for supervised paper testing (advisory warnings)"
        : "🟡 Not yet ready — run required harnesses";

  const schedulerRunning = !!sHealth?.running;

  return (
    <div className="pt-3 space-y-4" data-testid="harness-panel">
      {/* Deployment Readiness + Scheduler cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {/* Readiness */}
        <div className={`border p-3 ${TONE_STYLES[readinessTone]}`} data-testid="harness-deploy-gate">
          <div className="flex items-center justify-between">
            <div className="text-[10px] mono uppercase tracking-widest text-muted-foreground">Deployment Readiness</div>
            <span className="text-[9px] mono uppercase tracking-widest text-muted-foreground" title="Not the same as System Health — measures whether required harnesses have been run.">
              ⓘ not system health
            </span>
          </div>
          <div className="mt-1 text-sm mono">{readinessLabel}</div>
          <div className="mt-3 space-y-1.5">
            {readinessRows.map(r => {
              const t = OVERALL_TONE[r.status] || "UNKNOWN";
              const passed = r.status === "PASS";
              return (
                <div key={r.sid} className="flex items-center gap-2 text-[11px]" data-testid={`ready-row-${r.sid}`}>
                  <span>{passed ? "✓" : (r.status === "FAIL" ? "✗" : "•")}</span>
                  <span className="mono">{r.label}</span>
                  <span className={`mono text-[10px] ml-auto ${TONE_STYLES[t]}`}>{r.status}</span>
                  {!passed && (
                    <button
                      onClick={() => runNow(r.sid)}
                      disabled={running === r.sid}
                      className="border border-border/60 px-2 py-0.5 text-[9px] uppercase tracking-widest hover:bg-border/20 disabled:opacity-40"
                      data-testid={`ready-run-${r.sid}`}>
                      {running === r.sid ? "…" : "Run Now"}
                    </button>
                  )}
                </div>
              );
            })}
            <div className="border-t border-border/40 pt-2 mt-1 space-y-1.5" data-testid="canonical-live-section">
              <div className="flex items-center gap-2 text-[11px]">
                <span>•</span>
                <span className="mono">{liveRow.label}</span>
                <span className={`mono text-[10px] uppercase tracking-widest ml-auto px-1.5 py-0.5 border ${
                  liveRow.state === "RUNNING" ? "border-yellow-500 text-yellow-500"
                  : liveRow.state === "ENABLED" ? "border-emerald-500 text-emerald-500"
                  : liveRow.state === "ENABLED_BLOCKED" ? "border-yellow-500 text-yellow-500"
                  : liveRow.state === "DISABLED" ? "border-border/60 text-muted-foreground"
                  : "border-border/60 text-muted-foreground"
                }`} data-testid="canonical-live-badge">
                  {liveRow.state === "ENABLED_BLOCKED" ? "ENABLED (BLOCKED)" : liveRow.state}
                </span>
              </div>
              {liveRow.enabled && liveRow.updated_at && (
                <div className="text-[9px] text-muted-foreground mono">
                  Enabled {relTime(liveRow.updated_at).rel} by {liveRow.updated_by || "owner"}
                </div>
              )}
              <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-[10px] mt-1">
                {liveRow.requirements.map((r, i) => (
                  <div key={i} className="flex items-center gap-1.5" data-testid={`canonical-live-req-${r.key}`}>
                    <span className={r.met ? "text-emerald-500" : "text-muted-foreground"}>
                      {r.met ? "✓" : "○"}
                    </span>
                    <span className={r.met ? "" : "text-muted-foreground"}>{r.label}</span>
                  </div>
                ))}
              </div>
              {/* Deployment Gate Diagnostics — expandable audit of every
                  gating rule. Answers "why is Canonical Live locked?" */}
              <DeploymentGateDiagnostics gate={gate} liveRow={liveRow} onUnblock={load} />
              <div className="flex items-center gap-1.5 pt-1">
                {liveRow.enabled ? (
                  <button
                    onClick={() => setConfirmAction("disable")}
                    className="border border-border/60 px-2 py-0.5 text-[10px] uppercase tracking-widest hover:bg-border/20"
                    data-testid="canonical-live-disable"
                  >
                    Disable Canonical Live
                  </button>
                ) : (
                  <button
                    onClick={() => setConfirmAction("enable")}
                    disabled={!liveRow.all_requirements_met}
                    className="border border-emerald-500 px-2 py-0.5 text-[10px] uppercase tracking-widest text-emerald-500 hover:bg-emerald-500/10 disabled:opacity-40 disabled:hover:bg-transparent disabled:cursor-not-allowed"
                    data-testid="canonical-live-enable"
                    title={liveRow.all_requirements_met ? "" : "All requirements must be met before enabling"}
                  >
                    Enable Canonical Live
                  </button>
                )}
                {liveRow.enabled && liveRow.all_requirements_met && !liveRow.running_run_id && (
                  <button
                    onClick={() => runNow("paper_trading_live")}
                    disabled={running === "paper_trading_live"}
                    className="border border-border/60 px-2 py-0.5 text-[10px] uppercase tracking-widest hover:bg-border/20 disabled:opacity-40"
                    data-testid="canonical-live-run"
                  >
                    {running === "paper_trading_live" ? "…" : "Run Supervised Test"}
                  </button>
                )}
                {liveRow.running_run_id && (
                  <button
                    onClick={() => openAuthedReport(liveRow.running_run_id, "html")}
                    className="border border-yellow-500 px-2 py-0.5 text-[10px] uppercase tracking-widest text-yellow-500 hover:bg-yellow-500/10"
                    data-testid="canonical-live-view-active"
                  >
                    View Active Run
                  </button>
                )}
              </div>
            </div>
          </div>
          {gate?.blocks?.length > 0 && (
            <div className="mt-3 pt-2 border-t border-border/40 space-y-1">
              {gate.blocks.slice(0, 6).map((b, i) => {
                const detail = (gate.block_details || [])[i];
                const rich = humanReadyDetail(detail);
                if (rich) {
                  return (
                    <div key={i} className="text-[10px] flex items-start gap-1.5 py-1 border-b border-border/20 last:border-b-0" data-testid="gate-block-rich">
                      <span className="pnl-down">⚠</span>
                      <div className="flex-1 space-y-0.5">
                        <div className="text-foreground">
                          <span className="font-medium">{rich.title}</span>
                          {rich.scenario && (
                            <span className="text-muted-foreground ml-1">
                              · {rich.scenario}
                            </span>
                          )}
                        </div>
                        {rich.subtitle && (
                          <div className="text-muted-foreground">{rich.subtitle}</div>
                        )}
                        {rich.reason && (
                          <div>
                            <span className="text-muted-foreground uppercase tracking-widest text-[9px]">Reason:</span>{" "}
                            <span className="pnl-down">{rich.reason}</span>
                          </div>
                        )}
                        {rich.recommended_action && (
                          <div className="text-muted-foreground italic">
                            {rich.recommended_action}
                          </div>
                        )}
                      </div>
                      {rich.run_id && (
                        <button
                          onClick={() => openAuthedReport(rich.run_id, "html")}
                          className="border border-border/60 px-1.5 py-0.5 text-[9px] uppercase tracking-widest hover:bg-border/20 shrink-0"
                          data-testid={`gate-view-details-${rich.run_id}`}
                        >
                          View Details
                        </button>
                      )}
                      {!rich.run_id && rich.run_sid && (
                        <button
                          onClick={() => runNow(rich.run_sid)}
                          disabled={running === rich.run_sid}
                          className="border border-border/60 px-1.5 py-0.5 text-[9px] uppercase tracking-widest hover:bg-border/20 disabled:opacity-40 shrink-0">
                          Run
                        </button>
                      )}
                    </div>
                  );
                }
                const h = humanReadyRow(b);
                return (
                  <div key={i} className="text-[10px] flex items-start gap-1.5" data-testid="gate-block-friendly">
                    <span className="pnl-down">⚠</span>
                    <div className="flex-1">
                      <div className="text-foreground">{h.title}</div>
                      {h.hint && <div className="text-muted-foreground italic">{h.hint}</div>}
                    </div>
                    {h.run_sid && (
                      <button
                        onClick={() => runNow(h.run_sid)}
                        disabled={running === h.run_sid}
                        className="border border-border/60 px-1.5 py-0.5 text-[9px] uppercase tracking-widest hover:bg-border/20 disabled:opacity-40 shrink-0">
                        Run
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Scheduler — friendly wording */}
        <div className={`border p-3 ${TONE_STYLES[schedulerRunning ? "HEALTHY" : "UNKNOWN"]}`} data-testid="harness-scheduler-health">
          <div className="text-[10px] mono uppercase tracking-widest text-muted-foreground">Scheduler</div>
          <div className="mt-1 flex items-center gap-2">
            <span className="text-lg">{schedulerRunning ? "🟢" : "○"}</span>
            <span className="text-sm mono">{schedulerRunning ? "Running" : "Disabled"}</span>
          </div>
          <div className="mt-1 text-[11px] text-muted-foreground">
            {schedulerRunning
              ? "Nightly automation is running. Manual runs remain available."
              : "Nightly automation not enabled. Manual runs available."}
          </div>
          <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-0.5 text-[11px] mono text-muted-foreground">
            <div title={relTime(sHealth?.last_heartbeat).abs}>Heartbeat: {relTime(sHealth?.last_heartbeat).rel}</div>
            <div>Active: {sHealth?.active_jobs ?? 0}</div>
            <div>Stuck: {sHealth?.stuck_jobs ?? 0}</div>
            <div>Missed: {sHealth?.missed_runs ?? 0}</div>
          </div>
        </div>
      </div>

      {/* PR gate button */}
      <div className="flex items-center gap-2">
        <button
          onClick={runPRGate}
          disabled={running === "pr_gate"}
          className="border border-border/60 px-3 py-1.5 text-[11px] mono uppercase tracking-widest hover:bg-border/20 disabled:opacity-50"
          data-testid="harness-pr-gate-btn">
          {running === "pr_gate" ? "Running…" : "Run PR Gate (mock-only)"}
        </button>
        <span className="text-[10px] mono text-muted-foreground">Fast failure_injection + short soak. No live Alpaca.</span>
      </div>

      {/* Schedules list */}
      <div className="border border-border/60" data-testid="harness-schedules">
        <div className="text-[10px] mono uppercase tracking-widest text-muted-foreground p-2 border-b border-border/60">Schedules</div>
        <table className="w-full text-[11px] mono">
          <thead>
            <tr className="text-left text-muted-foreground">
              <th className="p-2">Schedule</th><th>Harness</th><th>Cadence</th>
              <th>Last Run</th><th>Result</th><th>Commit</th><th></th>
            </tr>
          </thead>
          <tbody>
            {schedules.map((s) => {
              const last = s.last_run || {};
              const tone = OVERALL_TONE[last.overall] || "UNKNOWN";
              const pending = s.pending_implementation;
              const isCanonicalLive = s.schedule_id === "paper_trading_live";
              const canonicalBadge = isCanonicalLive
                ? liveRow.state === "ENABLED_BLOCKED" ? "ENABLED (BLOCKED)"
                : liveRow.state
                : null;
              const t = relTime(last.started_at);
              return (
                <tr key={s.schedule_id} className="border-t border-border/60" data-testid={`harness-schedule-row-${s.schedule_id}`}>
                  <td className="p-2">{HARNESS_LABELS[s.schedule_id] || s.schedule_id}</td>
                  <td>{s.harness} / {s.mode}</td>
                  <td>{Math.round((s.cadence_secs || 0) / 60)} min</td>
                  <td title={t.abs}>{t.rel}</td>
                  <td>
                    {isCanonicalLive ? (
                      <span
                        className={`text-[10px] mono uppercase tracking-widest px-1 py-0.5 border ${
                          liveRow.state === "RUNNING" ? "border-yellow-500 text-yellow-500"
                          : liveRow.state === "ENABLED" ? "border-emerald-500 text-emerald-500"
                          : liveRow.state === "ENABLED_BLOCKED" ? "border-yellow-500 text-yellow-500"
                          : "border-border/60 text-muted-foreground"
                        }`}
                        data-testid={`schedule-canonical-badge-${s.schedule_id}`}
                        title="Owner-flippable via the Canonical Live section above"
                      >
                        {canonicalBadge}
                      </span>
                    ) : pending ? (
                      <span className="text-muted-foreground">Unavailable</span>
                    ) : (
                      <span className={TONE_STYLES[tone]}>{TONE_DOT[tone]} {last.overall || "—"}</span>
                    )}
                    {last.cleanup_status === "FAILED_CLEANUP" && <span className="pnl-down ml-1">CLEANUP</span>}
                  </td>
                  <td>{last.git_commit || "—"}</td>
                  <td>
                    <button
                      onClick={() => runNow(s.schedule_id)}
                      disabled={running === s.schedule_id || pending}
                      className="border border-border/60 px-2 py-0.5 text-[10px] uppercase tracking-widest hover:bg-border/20 disabled:opacity-40"
                      title={pending ? "Canonical Live is disabled — enable it from the section above" : ""}
                      data-testid={`harness-run-btn-${s.schedule_id}`}>
                      {running === s.schedule_id ? "…" : (pending ? (isCanonicalLive ? "Disabled" : "Locked") : "Run")}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Recent runs */}
      <div className="border border-border/60" data-testid="harness-runs">
        <div className="text-[10px] mono uppercase tracking-widest text-muted-foreground p-2 border-b border-border/60">Recent Runs</div>
        <table className="w-full text-[11px] mono">
          <thead>
            <tr className="text-left text-muted-foreground">
              <th className="p-2">Started</th><th>Harness</th><th>Trigger</th>
              <th>Overall</th><th>Category</th><th>Duration</th><th>Commit</th><th>Report</th>
            </tr>
          </thead>
          <tbody>
            {runs.map((r) => {
              const tone = OVERALL_TONE[r.overall] || "UNKNOWN";
              const t = relTime(r.started_at);
              const diag = r.failure_diagnostic;
              const isExpanded = expandedRun === r.id;
              return (
                <>
                <tr key={r.id} className="border-t border-border/60">
                  <td className="p-2" title={t.abs}>{t.rel}</td>
                  <td>{HARNESS_LABELS[r.schedule_id] || r.schedule_id}</td>
                  <td>{r.trigger || "—"}</td>
                  <td><span className={TONE_STYLES[tone]}>{TONE_DOT[tone]} {r.overall || r.status}</span></td>
                  <td>
                    {diag ? (
                      <button
                        type="button"
                        onClick={() => setExpandedRun(isExpanded ? null : r.id)}
                        className="text-left hover:underline pnl-down"
                        data-testid={`harness-diagnostic-toggle-${r.id}`}
                        title="Click to expand diagnostic"
                      >
                        <div className="text-[11px] font-medium">
                          {r.schedule_id === "failure_injection"
                            ? "Failure Injection"
                            : (HARNESS_LABELS[r.schedule_id] || r.schedule_id)}
                          {diag.scenario_index && (
                            <span className="text-muted-foreground ml-1">
                              Scenario {diag.scenario_index} of {diag.scenario_total}
                            </span>
                          )}
                        </div>
                        <div className="text-[10px] text-muted-foreground">
                          {diag.scenario_title || diag.step}
                        </div>
                        {diag.root_cause && (
                          <div className="text-[10px]">
                            <span className="text-muted-foreground">Reason:</span>{" "}
                            {diag.root_cause}
                          </div>
                        )}
                      </button>
                    ) : (r.failure_category || "—")}
                  </td>
                  <td>{r.duration_secs != null ? `${r.duration_secs.toFixed(2)}s` : "—"}</td>
                  <td>{r.git_commit || "—"}</td>
                  <td className="flex gap-1 py-1">
                    {r.report_html_path && (
                      <button
                          onClick={() => openAuthedReport(r.id, "html")}
                          className="border border-border/60 px-1.5 py-0.5 text-[9px] uppercase tracking-widest hover:bg-border/20"
                          data-testid={`harness-report-html-${r.id}`}>
                        HTML
                      </button>
                    )}
                    {r.report_json_path && (
                      <button
                          onClick={() => openAuthedReport(r.id, "json")}
                          className="border border-border/60 px-1.5 py-0.5 text-[9px] uppercase tracking-widest hover:bg-border/20">
                        JSON
                      </button>
                    )}
                  </td>
                </tr>
                {isExpanded && diag && (
                  <tr key={`${r.id}-diag`} className="border-t border-border/40 bg-muted/30">
                    <td colSpan="8" className="p-4">
                      <div className="max-w-3xl space-y-2" data-testid={`harness-diagnostic-${r.id}`}>
                        <div className="text-[10px] mono uppercase tracking-widest text-muted-foreground">
                          {r.schedule_id === "failure_injection"
                            ? "Failure Injection"
                            : (HARNESS_LABELS[r.schedule_id] || r.schedule_id)}
                          {diag.scenario_index && (
                            <> · Scenario {diag.scenario_index} of {diag.scenario_total}</>
                          )}
                        </div>
                        <div className="text-sm font-medium">
                          {diag.scenario_title || diag.step}
                        </div>
                        {diag.injected_fault && (
                          <div className="text-xs">
                            <span className="text-muted-foreground uppercase tracking-widest text-[10px]">Injected fault</span>
                            <div className="mono">{diag.injected_fault}</div>
                          </div>
                        )}
                        {diag.expected && (
                          <div className="text-xs">
                            <span className="text-muted-foreground uppercase tracking-widest text-[10px]">Expected</span>
                            <div className="mono">{diag.expected}</div>
                          </div>
                        )}
                        {diag.actual && (
                          <div className="text-xs">
                            <span className="text-muted-foreground uppercase tracking-widest text-[10px]">Actual</span>
                            <div className="mono pnl-down">{diag.actual}</div>
                          </div>
                        )}
                        {(diag.recovery_attempted !== null && diag.recovery_attempted !== undefined) && (
                          <div className="text-xs">
                            <span className="text-muted-foreground uppercase tracking-widest text-[10px]">Recovery attempted</span>
                            <div className="mono">{diag.recovery_attempted ? "Yes" : "No"}</div>
                          </div>
                        )}
                        {(diag.recovery_successful !== null && diag.recovery_successful !== undefined) && (
                          <div className="text-xs">
                            <span className="text-muted-foreground uppercase tracking-widest text-[10px]">Recovery successful</span>
                            <div className={`mono ${diag.recovery_successful ? "pnl-up" : "pnl-down"}`}>
                              {diag.recovery_successful ? "Yes" : "No"}
                            </div>
                          </div>
                        )}
                        {diag.root_cause && (
                          <div className="text-xs">
                            <span className="text-muted-foreground uppercase tracking-widest text-[10px]">Root cause</span>
                            <div className="mono">{diag.root_cause}</div>
                          </div>
                        )}
                        {diag.recommended_action && (
                          <div className="text-xs">
                            <span className="text-muted-foreground uppercase tracking-widest text-[10px]">Recommended action</span>
                            <div className="mono">{diag.recommended_action}</div>
                          </div>
                        )}
                        {diag.has_stack_trace && r.report_html_path && (
                          <div className="pt-1">
                            <button
                              onClick={() => openAuthedReport(r.id, "html")}
                              className="text-[10px] uppercase tracking-widest border border-border/60 px-2 py-1 hover:bg-border/20"
                              data-testid={`harness-view-stack-${r.id}`}
                            >
                              View stack trace
                            </button>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
                </>
              );
            })}
            {runs.length === 0 && (
              <tr><td colSpan="8" className="p-3 text-center text-muted-foreground italic">No runs yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Confirmation dialog for Enable / Disable Canonical Live */}
      {confirmAction && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70"
          onClick={() => setConfirmAction(null)}
          data-testid="canonical-live-confirm-backdrop"
        >
          <div
            className="border border-border/60 bg-background p-4 max-w-md w-[90%] space-y-3"
            onClick={(e) => e.stopPropagation()}
            data-testid="canonical-live-confirm-dialog"
          >
            <div className="text-[10px] mono uppercase tracking-widest text-muted-foreground">
              {confirmAction === "enable" ? "Enable Canonical Live" : "Disable Canonical Live"}
            </div>
            <div className="text-sm">
              {confirmAction === "enable" ? (
                <>
                  This unlocks the Canonical Live Alpaca Paper harness. The harness
                  will submit real orders to your <span className="mono">alpaca_paper</span>{" "}
                  account when triggered — protected only by symbol whitelist,
                  quantity caps, and the daily-order limit.
                  <div className="mt-2 text-muted-foreground text-xs">
                    You can disable it again at any time. This is not a live-money
                    account.
                  </div>
                </>
              ) : (
                <>
                  Disabling will immediately lock the Canonical Live harness from
                  running or being triggered by the scheduler.
                  <div className="mt-2 text-muted-foreground text-xs">
                    Any active run continues to completion. Only new runs are blocked.
                  </div>
                </>
              )}
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <button
                onClick={() => setConfirmAction(null)}
                className="border border-border/60 px-2 py-0.5 text-[10px] uppercase tracking-widest hover:bg-border/20"
                data-testid="canonical-live-confirm-cancel"
              >
                Cancel
              </button>
              <button
                onClick={() => flipCanonicalLive(confirmAction === "enable")}
                className={`border px-2 py-0.5 text-[10px] uppercase tracking-widest ${
                  confirmAction === "enable"
                    ? "border-emerald-500 text-emerald-500 hover:bg-emerald-500/10"
                    : "border-yellow-500 text-yellow-500 hover:bg-yellow-500/10"
                }`}
                data-testid="canonical-live-confirm-ok"
              >
                {confirmAction === "enable" ? "Yes, Enable" : "Yes, Disable"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

